import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { useLanguage } from '../contexts/LanguageContext';
import { watchWebUpdates, applyWebUpdate } from '../services/webUpdateService';
import { WebRelease } from '../types/webRelease';
import { WebUpdateError, WebUpdateErrorCode } from '../utils/webUpdateError';

export default function WebUpdateBanner() {
  const { t } = useLanguage();
  const [release, setRelease] = useState<WebRelease | null>(null);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<WebUpdateErrorCode | 'error' | null>(null);
  useEffect(
    () => (Platform.OS === 'web' ? watchWebUpdates(setRelease) : undefined),
    [],
  );
  useEffect(() => {
    setError(null);
  }, [release?.buildId]);
  if (Platform.OS !== 'web' || !release) return null;
  const update = async () => {
    if (updating) return;
    setUpdating(true);
    setError(null);
    try {
      await applyWebUpdate(release);
    } catch (error) {
      setError(error instanceof WebUpdateError ? error.code : 'error');
      setUpdating(false);
    }
  };
  return (
    <View
      style={styles.banner}
      accessibilityRole="alert"
      accessibilityLiveRegion="polite"
    >
      <View style={styles.content}>
        <Text style={styles.title}>
          {t('webUpdate.title', release.version)}
        </Text>
        <Text style={styles.message}>{t('webUpdate.message')}</Text>
        {error && <Text style={styles.error}>{t(`webUpdate.${error}`)}</Text>}
      </View>
      <Pressable
        accessibilityRole="button"
        disabled={updating}
        onPress={update}
        style={[styles.button, updating && styles.busy]}
      >
        {updating && <ActivityIndicator color="#1E3A8A" size="small" />}
        <Text style={styles.buttonText}>
          {t(updating ? 'webUpdate.updating' : 'webUpdate.refresh')}
        </Text>
      </Pressable>
    </View>
  );
}
const styles = StyleSheet.create({
  banner: {
    backgroundColor: '#1E40AF',
    paddingHorizontal: 20,
    paddingVertical: 16,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 16,
    borderBottomWidth: 3,
    borderBottomColor: '#FBBF24',
  },
  content: { flexGrow: 1, flexShrink: 1, flexBasis: 260 },
  title: { color: '#FFF', fontWeight: '700', fontSize: 17 },
  message: { color: '#DBEAFE', fontSize: 14, marginTop: 4 },
  error: { color: '#FEF3C7', marginTop: 8, fontWeight: '600' },
  button: {
    backgroundColor: '#FFF',
    borderRadius: 8,
    paddingHorizontal: 18,
    paddingVertical: 12,
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center',
  },
  buttonText: { color: '#1E3A8A', fontWeight: '700', fontSize: 15 },
  busy: { opacity: 0.85 },
});
